<script setup lang="ts">
import { ref, watch, computed, onMounted } from 'vue'
import type { SaleTransaction } from '@/types/farmsales'
import type { Farm } from '@/types/Farm'
import type { FarmYield } from '@/types/farmYield'
import { api } from '@/api/auth'
import SuccessNotification from '@/components/SuccessNotification.vue'

interface Props {
  transaction: SaleTransaction | null
}
const props = defineProps<Props>()
const emit = defineEmits(['close', 'save'])

// Notification state
const showSuccess = ref(false)
const notificationMessage = ref('')

// form state
const form = ref<Partial<SaleTransaction>>({
  farm: '',
  yield_record: '',
  quantity: 0,
  unit_price: 0,
  buyer_name: '',
  payment_method: '',
})

// Form validation
const errors = ref({
  farm: '',
  yield_record: '',
  quantity: '',
  unit_price: '',
  buyer_name: '',
  payment_method: '',
})

const farms = ref<Farm[]>([])
const yields = ref<FarmYield[]>([])
const isLoading = ref(false)

// Load farms on mount
onMounted(async () => {
  try {
    isLoading.value = true
    const res = await api.get('farms/')
    farms.value = res.data.results
  } catch (error) {
    console.error('Failed to load farms:', error)
  } finally {
    isLoading.value = false
  }
})

watch(
  () => form.value.farm,
  async (farmId, oldFarmId) => {
    // Only reset yield if farm actually changed (not during initial load)
    if (farmId && farmId !== oldFarmId) {
      try {
        isLoading.value = true
        const { data } = await api.get(`farm/yields/?farm=${farmId}`)
        yields.value = data.results
        // Only reset if we're not editing an existing transaction
        if (!props.transaction) {
          form.value.yield_record = ''
        }
      } catch (error) {
        console.error('Failed to load yields:', error)
        yields.value = []
      } finally {
        isLoading.value = false
      }
    }
  },
)

// Sync props.transaction → form
watch(
  () => props.transaction,
  (tx) => {
    if (tx) {
      form.value = {
        ...tx,
        yield_record: typeof tx.yield_record === 'object' ? tx.yield_record : tx.yield_record,
        farm: typeof tx.farm === 'object' ? tx.farm : tx.farm,
      }
    } else {
      form.value = {
        farm: '',
        yield_record: '',
        quantity: 0,
        unit_price: 0,
        buyer_name: '',
        payment_method: '',
      }
    }
    // Reset errors when transaction changes
    errors.value = {
      farm: '',
      yield_record: '',
      quantity: '',
      unit_price: '',
      buyer_name: '',
      payment_method: '',
    }
  },
  { immediate: true },
)

const isEdit = computed(() => !!props.transaction)

// Validate form
const validateForm = (): boolean => {
  let isValid = true
  const newErrors = {
    farm: '',
    yield_record: '',
    quantity: '',
    unit_price: '',
    buyer_name: '',
    payment_method: '',
  }

  if (!form.value.farm) {
    newErrors.farm = 'Farm selection is required'
    isValid = false
  }

  if (!form.value.yield_record) {
    newErrors.yield_record = 'Crop selection is required'
    isValid = false
  }

  if (!form.value.quantity || form.value.quantity <= 0) {
    newErrors.quantity = 'Valid quantity is required'
    isValid = false
  }

  if (!form.value.unit_price || form.value.unit_price <= 0) {
    newErrors.unit_price = 'Valid unit price is required'
    isValid = false
  }

  if (!form.value.buyer_name?.trim()) {
    newErrors.buyer_name = 'Buyer name is required'
    isValid = false
  }

  if (!form.value.payment_method) {
    newErrors.payment_method = 'Payment method is required'
    isValid = false
  }

  errors.value = newErrors
  return isValid
}

const handleSubmit = () => {
  if (!validateForm()) return

  emit('save', { ...form.value })
  notificationMessage.value = isEdit.value
    ? 'Sale updated successfully!'
    : 'Sale recorded successfully!'
  showSuccess.value = true

  // Let the notification show for 4 seconds before closing the modal
  setTimeout(() => {
    // Wait a bit more before closing to ensure smooth transition
    setTimeout(() => {
      showSuccess.value = false

      emit('close')
    }, 300)
  }, 4000)
}
// const handleSubmit = () => {
//   if (!validateForm()) return

//   emit('save', { ...form.value })
//   notificationMessage.value = isEdit.value
//     ? 'Sale updated successfully!'
//     : 'Sale recorded successfully!'
//   showSuccess.value = true

//   // Don't close automatically — user will click "Cancel" or the X
// }
</script>

<template>
  <div class="modal-overlay">
    <div class="modal-container">
      <div class="modal-header">
        <h2>{{ isEdit ? 'Edit Sale' : 'New Sale' }}</h2>
        <button @click="emit('close')" class="close-button">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      <form @submit.prevent="handleSubmit" class="form">
        <!-- Farm Selection -->
        <div class="form-group">
          <label for="farm">Farm <span class="required">*</span></label>
          <select
            id="farm"
            v-model="form.farm"
            :class="['form-input', { error: errors.farm }]"
            :disabled="isLoading"
          >
            <option value="" disabled>Select Farm</option>
            <option v-for="f in farms" :key="f.id" :value="f.id">
              {{ f.name }}
            </option>
          </select>
          <span v-if="errors.farm" class="error-text">{{ errors.farm }}</span>
        </div>

        <!-- Yield Selection -->
        <div class="form-group">
          <label for="yield">Crop <span class="required">*</span></label>
          <select
            id="yield"
            v-model="form.yield_record"
            :class="['form-input', { error: errors.yield_record }]"
            :disabled="!form.farm || isLoading"
          >
            <option value="" disabled>Select Crop</option>
            <option v-for="y in yields" :key="y.id" :value="y.id">
              {{ y.crop_type }} ({{ y.quantity_harvested }} {{ y.units }} in stock since
              {{ new Date(y.harvest_date).toLocaleDateString() }})
            </option>
          </select>
          <span v-if="errors.yield_record" class="error-text">{{ errors.yield_record }}</span>
        </div>

        <!-- Quantity -->
        <div class="form-group">
          <label for="quantity">Quantity Sold <span class="required">*</span></label>
          <input
            id="quantity"
            v-model.number="form.quantity"
            type="number"
            min="0"
            step="0.01"
            :class="['form-input', { error: errors.quantity }]"
          />
          <span v-if="errors.quantity" class="error-text">{{ errors.quantity }}</span>
        </div>

        <!-- Unit Price -->
        <div class="form-group">
          <label for="unit-price">Unit Selling Price <span class="required">*</span></label>
          <input
            id="unit-price"
            v-model.number="form.unit_price"
            type="number"
            min="0"
            step="0.01"
            :class="['form-input', { error: errors.unit_price }]"
          />
          <span v-if="errors.unit_price" class="error-text">{{ errors.unit_price }}</span>
        </div>

        <!-- Buyer Name -->
        <div class="form-group">
          <label for="buyer">Buyer Name <span class="required">*</span></label>
          <input
            id="buyer"
            v-model="form.buyer_name"
            type="text"
            placeholder="Enter buyer name"
            :class="['form-input', { error: errors.buyer_name }]"
          />
          <span v-if="errors.buyer_name" class="error-text">{{ errors.buyer_name }}</span>
        </div>

        <!-- Payment Method -->
        <div class="form-group">
          <label for="payment">Payment Method <span class="required">*</span></label>
          <select
            id="payment"
            v-model="form.payment_method"
            :class="['form-input', { error: errors.payment_method }]"
          >
            <option value="" disabled>Select Payment Method</option>
            <option value="CASH">Cash</option>
            <option value="MPESA">M-Pesa</option>
            <option value="BANK">Bank Transfer</option>
            <option value="MOBILE">Mobile Money</option>
          </select>
          <span v-if="errors.payment_method" class="error-text">{{ errors.payment_method }}</span>
        </div>

        <!-- Total Price (computed) -->
        <div class="total-price">
          <strong>Total Price: </strong>
          {{
            form.quantity && form.unit_price ? (form.quantity * form.unit_price).toFixed(2) : '0.00'
          }}
        </div>

        <div class="form-actions">
          <button type="button" @click="emit('close')" class="btn-secondary">Cancel</button>
          <button type="submit" class="btn-primary">{{ isEdit ? 'Update' : 'Create' }} Sale</button>
        </div>
      </form>
    </div>

    <!-- Success Notification -->
    <SuccessNotification v-if="showSuccess" :message="notificationMessage" :duration="4000" />
  </div>
</template>

<style scoped>
/* Your existing styles remain the same */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 1rem;
}

.modal-container {
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  animation: modal-appear 0.3s ease-out;
}

@keyframes modal-appear {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 1.5rem 0;
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 1rem;
}

.modal-header h2 {
  font-size: 1.5rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
}

.close-button {
  background: none;
  border: none;
  cursor: pointer;
  color: #6b7280;
  padding: 0.25rem;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-button:hover {
  color: #374151;
  background-color: #f3f4f6;
}

.form {
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

label {
  font-weight: 500;
  color: #374151;
  font-size: 0.875rem;
}

.required {
  color: #ef4444;
}

.form-input {
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 1rem;
  transition:
    border-color 0.2s,
    box-shadow 0.2s;
}

.form-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.2);
}

.form-input.error {
  border-color: #ef4444;
}

.form-input:disabled {
  background-color: #f9fafb;
  cursor: not-allowed;
}

.error-text {
  color: #ef4444;
  font-size: 0.75rem;
  margin-top: 0.25rem;
}

.total-price {
  padding: 0.75rem;
  background-color: #f9fafb;
  border-radius: 6px;
  text-align: center;
  font-size: 1.125rem;
  color: #059669;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1rem;
}

.btn-secondary,
.btn-primary {
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition:
    background-color 0.2s,
    transform 0.1s;
}

.btn-secondary:active,
.btn-primary:active {
  transform: scale(0.98);
}

.btn-secondary {
  background-color: white;
  border: 1px solid #d1d5db;
  color: #374151;
}

.btn-secondary:hover {
  background-color: #f9fafb;
}

.btn-primary {
  background-color: #2563eb;
  border: none;
  color: white;
}

.btn-primary:hover {
  background-color: #1d4ed8;
}

/* Responsive adjustments */
@media (max-width: 640px) {
  .modal-container {
    margin: 0;
    border-radius: 0;
    max-height: 100vh;
  }

  .form-actions {
    flex-direction: column;
  }

  .btn-secondary,
  .btn-primary {
    width: 100%;
  }
}
</style>
